import { CloseCircleOutlined, CloseOutlined } from '@ant-design/icons';
import { useClickOutside } from '@mm/hooks';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';
import { ArrowStatefulIcon, CloseCircleIcon } from '@mm/icons';
import { colors } from '@mm/utils';
import { Checkbox, ConfigProvider } from 'antd';
import classNames from 'classnames';
import { isEqual } from 'lodash';
import React, {
  Dispatch,
  MouseEvent,
  ReactNode,
  SetStateAction,
  useEffect,
  useState,
} from 'react';
import { Waypoint } from 'react-waypoint';
import { RadioInput, ThreeDotMenu, ThreeDotMenuProps } from '..';
import { ScrollArea } from '../ScrollArea/ScrollArea';
import { SearchInput } from '../SearchInput/SearchInput';
import { Spinner } from '../Spinner/Spinner';

import './Select.pcss';

export type Option<T = string | number> = {
  id: T;
  label: JSX.Element | Element | string;
  toString?: () => string;
  disabled?: boolean;
  tooltip?: ReactNode;
  heading?: boolean;
} & Record<string, any>;

type Props<T extends Option> = {
  className?: string;
  label?: string;
  placeholder?: string;
  selected?: T[];
  options?: T[];
  isLoading?: boolean;
  disabled?: boolean;
  onSearch?: (query?: string) => void;
  onChange?: (v: T[]) => void;
  onOpen?: () => void;
  onClose?: () => void;
  onScrollEnd?: () => void;
  withThreeDotMenu?: boolean;
  menuProps?: ThreeDotMenuProps;
  setAudienceId?: Dispatch<SetStateAction<number>>;
  hideClearButton?: boolean; // Hide the clear button which empties the current selection
  hideCount?: boolean; // Hide the counter showing the number of items selected
  singleSelect?: boolean; // Only allow a single selection
  disableClickOutsideExceptions?: boolean; // Don't leave this dropdown open if another one is opened
  staticDropdownPositioning?: boolean; // Freezes the location of the dropdown at the time it is opened
  useNbspInLabels?: boolean;
  withSelectAll?: boolean;
  selectAllLabel?: string;
};
const remapSpaceCharacters = label => label.replaceAll(' ', '\xa0');

// tslint:disable-next-line:cyclomatic-complexity
export const Select = <T extends Option>({
  options = [],
  selected = [],
  className,
  label,
  placeholder,
  disabled,
  onSearch,
  onChange = () => {},
  onOpen = () => {},
  onClose = () => {},
  onScrollEnd = () => {},
  isLoading,
  withThreeDotMenu,
  menuProps,
  setAudienceId,
  hideClearButton,
  hideCount,
  singleSelect,
  disableClickOutsideExceptions,
  staticDropdownPositioning,
  useNbspInLabels,
  withSelectAll,
  selectAllLabel,
}: Props<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const isAllSelected = isEqual(
    selected.map(option => option.id).sort(),
    options.map(option => option.id).sort(),
  );

  const { primaryColor, isWhitelabelCompany } = useWhitelabelConfig();

  const open = () => {
    if (!disabled) {
      setIsOpen(true);
      onOpen();
    }
  };

  const close = () => {
    if (!disabled) {
      setIsOpen(false);
      setSearch('');
      onClose();
    }
  };

  const onSelectClick = () => {
    if (isOpen) {
      return close();
    }

    return open();
  };

  const onSearchChange = e => {
    setSearch(e.target.value);
    onSearch?.(e.target.value);
  };

  const ref = useClickOutside<HTMLDivElement>((e: Event) => {
    if (!disableClickOutsideExceptions) {
      const exception = e
        .composedPath()
        .some((e: Element) => e.className === 'c-select__select');

      if (exception) {
        return;
      }
    }

    close();
  });

  const withConfigProvider = (children: ReactNode) => (
    <ConfigProvider
      theme={{
        components: {
          Checkbox: {
            colorPrimary: isWhitelabelCompany
              ? primaryColor
              : 'var(--color-geekblue-6)',
          },
        },
      }}
    >
      {children}
    </ConfigProvider>
  );

  const handleSelect = (opt: Option) => {
    if (opt.disabled || opt.heading) {
      return;
    }

    if (setAudienceId) {
      setAudienceId(Number(opt.id));
    }
    const toAdd = options.find(v => v.id === opt.id);
    if (singleSelect && toAdd) {
      onChange([toAdd]);
      close();
      return;
    }
    const toRemove = selected.some(v => v.id === opt.id);

    const without = selected.filter(v => v.id !== opt.id);

    if (toRemove) {
      return onChange(without);
    }

    if (toAdd) {
      onChange([...without, toAdd]);
    }
  };

  const onSelectAll = () => {
    if (isAllSelected) {
      onChange([]);
    } else {
      onChange([...options]);
    }
  };

  const handleRemove = (e: MouseEvent<HTMLDivElement>) => {
    if (!hideClearButton && !disabled && selected.length) {
      onChange([]);
      e.stopPropagation();
    }
  };
  const [dropdownStyle, setDropdownStyle] = useState({});
  useEffect(() => {
    if (staticDropdownPositioning && isOpen) {
      const {
        offsetTop = 0,
        offsetHeight = 0,
        offsetLeft = 0,
      } = ref.current?.parentElement || {};
      setDropdownStyle({
        top: offsetTop + offsetHeight,
        left: offsetLeft,
      });
    } else {
      setDropdownStyle({});
    }
  }, [isOpen, ref, staticDropdownPositioning]);

  const [tooltip, setTooltip] = useState<ReactNode | undefined>();

  useEffect(() => {
    const ev = () => {
      setTooltip(undefined);
    };

    document.addEventListener('click', ev);

    return document.removeEventListener('click', ev);
  }, []);

  return (
    <div
      className={classNames(className, 'c-select__container', {
        'c-select--focused': isOpen,
        'c-select--disabled': disabled,
        'c-select--staticDropdownPositioning': staticDropdownPositioning,
      })}
    >
      <div className="c-select__select" onClick={onSelectClick}>
        {label && <span className="c-select__label">{label}</span>}

        {!selected.length && placeholder && (
          <span className="c-select__placeholder">{placeholder}</span>
        )}

        {!!selected.length && (
          <span className="c-select__value">
            {isAllSelected && withSelectAll
              ? selectAllLabel || 'Select All'
              : selected
                  .map(opt =>
                    typeof opt.label === 'string' ? opt.label : opt.toString(),
                  )
                  .map(useNbspInLabels ? remapSpaceCharacters : v => v)
                  .join(', ')}
          </span>
        )}

        {!hideCount && !!selected.length && (
          <span className="c-select__count">{selected.length}</span>
        )}

        <div className="c-select__icon" onClick={handleRemove}>
          {!hideClearButton && selected?.length ? (
            <CloseOutlined className="c-select__close-icon" />
          ) : (
            <ArrowStatefulIcon
              color="#262626"
              size={14}
              direction={isOpen ? 'up' : 'down'}
            />
          )}
        </div>
      </div>

      {isOpen && (
        <div className="c-select__dropdown" ref={ref} style={dropdownStyle}>
          {onSearch && (
            <span className="c-select__search">
              <SearchInput
                css={{ container: 'c-select__search-input' }}
                placeholder="Search: "
                value={search}
                onChange={onSearchChange}
              />
            </span>
          )}

          {tooltip && (
            <span className="c-select__option-tooltip">
              <span className="c-select__option-tooltip-content">
                {tooltip}
              </span>
            </span>
          )}

          <ScrollArea className="c-select__list">
            {withSelectAll &&
              withConfigProvider(
                <Checkbox
                  indeterminate={Boolean(selected.length) && !isAllSelected}
                  onChange={onSelectAll}
                  checked={isAllSelected}
                  disabled={options.every(
                    option => option.disabled || option.heading,
                  )}
                  className={classNames(
                    'c-select__select-all',
                    'c-select__checkbox',
                    'c-select__option',
                  )}
                >
                  All Accounts
                </Checkbox>,
              )}
            {!(search && isLoading) &&
              options.map(option => {
                const isSelected = selected?.some(({ id }) => id === option.id);
                return (
                  <div
                    key={option.id}
                    className={classNames('c-select__item', {
                      'c-select__selected': isSelected,
                      'c-select__item-heading': option.heading,
                    })}
                    onMouseEnter={() => {
                      setTooltip(option.tooltip);
                    }}
                  >
                    {option.heading ? (
                      option.label
                    ) : singleSelect ? (
                      <div
                        onClick={() => handleSelect(option)}
                        className={classNames('c-select__option', {
                          'c-select__option--selected': isSelected,
                        })}
                      >
                        {useNbspInLabels
                          ? remapSpaceCharacters(option.label)
                          : option.label}
                      </div>
                    ) : (
                      withConfigProvider(
                        <Checkbox
                          onChange={() => handleSelect(option)}
                          checked={isSelected}
                          disabled={option.disabled}
                          className="c-select__option"
                        >
                          {useNbspInLabels
                            ? remapSpaceCharacters(option.label)
                            : option.label}
                        </Checkbox>,
                      )
                    )}

                    {withThreeDotMenu && menuProps && (
                      <ThreeDotMenu
                        className={menuProps.className}
                        elements={menuProps.elements}
                        selectItem={menuProps.selectItem}
                        contentClassName={menuProps.contentClassName}
                      />
                    )}
                  </div>
                );
              })}
            {!isLoading && onScrollEnd && (
              <Waypoint bottomOffset="-20px" onEnter={onScrollEnd} />
            )}
            {!selected?.length && isLoading && (
              <span className="c-select__loader">
                <Spinner
                  show
                  size={14}
                  position="relative"
                  color={colors.accentBlue}
                  backgroundColor="transparent"
                />
              </span>
            )}
          </ScrollArea>
        </div>
      )}
    </div>
  );
};
